import "../../packages/ui/src/styles/index.scss";

import { pushVirtualPageView } from "@repo/analytics";

export const onClientEntry = () => {
  // Add a site-specific className to <html>
  document.documentElement.classList.add("hcp-ppl");

  // Prevent browser history from restoring scroll positions on SPA navigation.
  if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
};

export const shouldUpdateScroll = ({ routerProps }) => {
  // Hash scrolling is handled in onRouteUpdate so it can stay smooth.
  if (routerProps?.location?.hash) {
    return false;
  }

  // Return false so Gatsby does not attempt any scroll management.
  // onRouteUpdate owns all scroll-to-top logic.
  return false;
};

const scrollToTop = () => {
  if (typeof window === "undefined") return;

  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
};

const getStickyHeaderOffset = () => {
  const utilityNav = document.querySelector(".utility-nav");
  const siteHeader = document.querySelector(".site-header");
  return (utilityNav?.offsetHeight || 0) + (siteHeader?.offsetHeight || 0);
};

const scrollToHash = (hash) => {
  if (typeof document === "undefined") return;

  const id = decodeURIComponent(hash.replace(/^#/, "").replace(/\/+$/, ""));
  if (!id) return;

  // The ISI section renders client-side after hydration, so on a direct load
  // the target may not exist yet when onRouteUpdate fires. Wait for it to
  // render AND for its position to stop moving (hydration / image loads),
  // then animate a single smooth scroll. ISI anchors intentionally ignore
  // the HCP intercept modal and its localStorage flag — the page scrolls
  // behind the modal so the ISI is in position when the modal is dismissed.
  const start = Date.now();
  const renderDeadline = start + 8000; // waiting for the target to exist
  const stableDeadline = start + 10000; // waiting for layout to settle

  const getTargetTop = (target) =>
    target.getBoundingClientRect().top +
    window.scrollY -
    getStickyHeaderOffset();

  let lastTop = null;
  let stableFrames = 0;
  const requiredStableFrames = 10; // ~160ms of layout holding still

  const attempt = () => {
    const target = document.getElementById(id);

    if (!target) {
      lastTop = null;
      stableFrames = 0;
      if (Date.now() < renderDeadline) {
        requestAnimationFrame(attempt);
      }
      return;
    }

    const top = getTargetTop(target);

    if (lastTop !== null && Math.abs(top - lastTop) <= 2) {
      stableFrames++;
    } else {
      stableFrames = 0;
    }
    lastTop = top;

    if (stableFrames >= requiredStableFrames || Date.now() > stableDeadline) {
      window.scrollTo({ top, behavior: "smooth" });
      watchForLateShifts(top);
      return;
    }

    requestAnimationFrame(attempt);
  };

  // Backstop for assets that finish loading after the scroll (cold cache):
  // if the target's position moves again, re-animate to it — unless the
  // user has started scrolling on their own.
  const watchForLateShifts = (scrolledTo) => {
    let expectedTop = scrolledTo;
    const settleDeadline = Date.now() + 3000;
    const hardDeadline = Date.now() + 10000;
    let cancelled = false;
    const cancel = () => {
      cancelled = true;
    };
    const userEvents = ["wheel", "touchstart", "keydown", "mousedown"];
    userEvents.forEach((event) =>
      window.addEventListener(event, cancel, { passive: true, once: true }),
    );

    const settle = () => {
      const expired =
        Date.now() > hardDeadline ||
        (Date.now() > settleDeadline && document.readyState === "complete");

      if (cancelled || expired) {
        userEvents.forEach((event) =>
          window.removeEventListener(event, cancel),
        );
        return;
      }

      const el = document.getElementById(id);
      if (el) {
        const top = getTargetTop(el);
        if (Math.abs(top - expectedTop) > 2) {
          expectedTop = top;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }

      requestAnimationFrame(settle);
    };

    requestAnimationFrame(settle);
  };

  attempt();
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  // Initial-load page context is pushed by the head init script;
  // only emit a virtual pageview for in-app (SPA) navigations.
  if (prevLocation) {
    pushVirtualPageView("hcp_ppl", location?.pathname);
  }

  if (location?.hash) {
    // scrollToHash polls until the target has rendered, so it can be
    // invoked immediately — it ignores the intercept modal/cookie.
    scrollToHash(location.hash);
    return;
  }

  // rAF x2 handles most cases; setTimeout catches any deferred
  // Gatsby scroll restoration that fires after the animation frames.
  requestAnimationFrame(() => {
    scrollToTop();
    requestAnimationFrame(() => {
      scrollToTop();
      setTimeout(scrollToTop, 0);
    });
  });
};
